import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { eAchPullPayerType } from 'types/order/escrow/eAchPullPayerType';
import { ePayerType } from 'types/order/escrow/ePayerType';
import { IWireDepositData } from 'types/order/escrow/IWireDepositData';
import { getEscrowBankAccountLabel } from 'ui-toolkit/utilities/getEscrowBankAccountLabel';
import { ICheckDepositData } from 'types/order/escrow/ICheckDeposit';
import { IEscrowExpectedIncomingAchData } from '../../../../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { IEscrowMatchAchPullDepositResponse } from 'types/accounting/response/IEscrowExpectedAchPullDepositResponse';

export interface IActualDepositData {
  matchedAt?: string;
  disbursedAt?: string;
  completedAt?: string;
  updatedAt: string;
  sender?: string;
  status: eMatchedQueryValue;
  matchedBy?: string;
  type: string;
  amountCents: number;
  receivingPartyName?: string;
  receivingPartyAccountType?: string;
  receivingPartyAccountNumber?: string;
  receivingPartyRoutingNumber?: string;
  wellsFargoTransactionNumber?: string;
  spruceAccount?: string;
  spruceAccountLabel?: string;
  spruceRoutingNumber?: string;
  memo?: string;
  payerType?: ePayerType | eAchPullPayerType;
  wireDetails?: string;
  checkDetails?: string;
  mismatched?: string;
  identifier?: string;
  actualAmountCents?: number;
  bankReference?: string;
  expectedCheckId?: string;
  actualCheckId?: string;
  expectedWireId?: string;
  actualWireId?: string;
  expectedAchId?: string;
  actualAchId?: string;
}
const achMatching = (ach: IEscrowExpectedIncomingAchData, match: IEscrowMatchAchPullDepositResponse) => {
  // We have to trace the accounts.
  return (
    match.actual?.escrowBankAccount.accountNumber === ach.escrowFile.depositEscrowBankAccount.accountNumber ||
    (match.actual?.escrowBankAccount.accountNumber === match.internalTransfer?.sourceAccount.accountNumber &&
      match.internalTransfer?.destinationAccount.accountNumber ===
        ach.escrowFile.depositEscrowBankAccount.accountNumber)
  );
};
export const convertAchDepositToExpectedDepositData = (
  achDeposit: IEscrowExpectedIncomingAchData,
  userIdToUserName: (id: string) => string,
  isSpruceWithZillowEnabled: boolean,
  isZillowClosingEnabled: boolean,
): IActualDepositData[] => {
  return achDeposit.matches.map((deposit) => ({
    matchedAt: deposit.createdAt,
    disbursedAt: achDeposit.processingForDisbursementStartedAt,
    completedAt: achDeposit.processingForDisbursementCompletedAt,
    updatedAt: achDeposit.updatedAt,
    sender: deposit.actual?.source,
    status: achMatching(achDeposit, deposit) ? eMatchedQueryValue.MATCHED : eMatchedQueryValue.MISMATCHED,
    matchedBy: userIdToUserName(deposit.createdBy),
    type: 'ACH',
    amountCents: achDeposit.amountCents,
    actualAmountCents: deposit.actual?.amountCents,
    receivingPartyName: achDeposit.achAccount?.name,
    receivingPartyAccountType: achDeposit.achAccount?.accountType,
    receivingPartyAccountNumber: achDeposit.bankAccount.accountNumber,
    receivingPartyRoutingNumber: achDeposit.bankAccount.fedAchRoutingNumberId,
    wellsFargoTransactionNumber: deposit.actual?.wellsFargoTransactionNumber,
    spruceAccount: achDeposit.bankAccount?.accountNumber,
    spruceAccountLabel: (() => {
      const bank = achDeposit.bankAccount;
      if (!bank) return undefined;
      const base = isSpruceWithZillowEnabled ? bank.legalName : bank.spruceName;
      const selectedNickname = isZillowClosingEnabled ? bank.nickname : bank.spruceNickname;
      return selectedNickname ? `${base} ${selectedNickname}` : base;
    })(),
    memo: achDeposit.memo,
    payerType: achDeposit.payerType,
    expectedAchId: achDeposit.id,
    actualAchId: deposit.actual?.id,
  }));
};

export const convertWireDepositToExpectedDepositData = (
  wireDeposit: IWireDepositData,
  isSpruceWithZillowEnabled: boolean,
  isZillowClosingEnabled: boolean,
): IActualDepositData => {
  return {
    matchedAt: wireDeposit.matchedAt,
    completedAt: wireDeposit.completedAtDate,
    updatedAt: wireDeposit.updatedAt,
    sender: wireDeposit.actualIncomingWire?.wireDetail.org,
    status: wireDeposit.matched,
    matchedBy: wireDeposit.matchedBy,
    type: 'Wire',
    amountCents: wireDeposit.amountCents,
    actualAmountCents: wireDeposit.actualIncomingWire?.amountCents,
    spruceAccountLabel: getEscrowBankAccountLabel(wireDeposit.escrowBankAccount, isSpruceWithZillowEnabled, isZillowClosingEnabled),
    spruceAccount: wireDeposit.actualIncomingWire?.bankAccount.accountNumber,
    spruceRoutingNumber: wireDeposit.actualIncomingWire?.bankAccount.fedWireRoutingNumber?.routingNumber,
    payerType: wireDeposit.payerType,
    wireDetails: JSON.stringify(wireDeposit.actualWireDetail),
    mismatched: wireDeposit.matched === eMatchedQueryValue.MISMATCHED ? 'Yes' : 'No',
    identifier: wireDeposit.wireIdentifier,
    expectedWireId: wireDeposit.id,
    actualWireId: wireDeposit.actualIncomingWire?.id,
  };
};

export const convertCheckDepositToExpectedDepositData = (checkDeposit: ICheckDepositData): IActualDepositData => {
  return {
    matchedAt: checkDeposit.matchedAt,
    completedAt: checkDeposit.actualIncomingCheck?.completedAtDate,
    updatedAt: checkDeposit.updatedAt,
    sender: JSON.stringify(checkDeposit.actualIncomingCheck?.checkDetail),
    status: checkDeposit.matched,
    matchedBy: checkDeposit.matchedBy,
    type: 'Check',
    amountCents: checkDeposit.amountCents,
    actualAmountCents: checkDeposit.actualIncomingCheck?.amountCents,
    payerType: checkDeposit.payerType,
    identifier: checkDeposit.actualIncomingCheck?.checkIdentifier,
    checkDetails: JSON.stringify(checkDeposit.actualIncomingCheck?.checkDetail),
    bankReference: checkDeposit.actualIncomingCheck?.bankRef,
    mismatched: checkDeposit.matched === eMatchedQueryValue.MISMATCHED ? 'Yes' : 'No',
    expectedCheckId: checkDeposit.id,
    actualCheckId: checkDeposit.actualIncomingCheck?.id,
  };
};
